import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Info } from "lucide-react";

type Channel = "inApp" | "email" | "push";

interface CategoryDef {
  key: string;
  group: string;
  label: string;
  description: string;
  audience: "user" | "admin";
  defaults: Record<Channel, boolean>;
}

interface PrefRow {
  category: string;
  inApp: boolean;
  email: boolean;
  push: boolean;
}

export function NotificationDefaultsCard() {
  const { toast } = useToast();
  const qc = useQueryClient();

  // Workspace-admin defaults card asks the catalog endpoint with the
  // `admin_defaults` scope so adminOnly rows (e.g. leadgen.low_credits,
  // manager.seat_request, recurring.*) are returned. The server only
  // honours the scope when the caller's *actual* workspace membership
  // role is "admin" — a non-admin sending ?scope=admin_defaults gets
  // the same trimmed list a regular user gets. The personal-settings
  // page calls the same URL without the scope param and never sees the
  // adminOnly rows, satisfying Task #8's "remove from user settings"
  // requirement for items like leadgen.low_credits / manager.seat_request.
  const { data: catsResp, isLoading: catsLoading } = useQuery<{ categories: CategoryDef[] }>({
    queryKey: ["/api/notifications/categories?scope=admin_defaults"],
  });

  const { data: defaultsResp, isLoading: defaultsLoading } = useQuery<{ workspaceDefaults: PrefRow[] }>({
    queryKey: ["/api/admin/notification-defaults"],
  });

  const [draft, setDraft] = useState<Record<string, Record<Channel, boolean>>>({});

  useEffect(() => {
    if (defaultsResp) {
      const map: Record<string, Record<Channel, boolean>> = {};
      for (const r of defaultsResp.workspaceDefaults || []) {
        map[r.category] = { inApp: r.inApp, email: r.email, push: r.push };
      }
      setDraft(map);
    }
  }, [defaultsResp]);

  const save = useMutation({
    mutationFn: () =>
      apiRequest("PUT", "/api/admin/notification-defaults", { preferences: draft }),
    onSuccess: () => {
      toast({ title: "Workspace defaults saved" });
      qc.invalidateQueries({ queryKey: ["/api/admin/notification-defaults"] });
    },
    onError: (e: any) =>
      toast({ title: "Failed to save", description: e?.message, variant: "destructive" }),
  });

  const grouped = useMemo(() => {
    const map: Record<string, CategoryDef[]> = {};
    for (const c of catsResp?.categories || []) {
      (map[c.group] ||= []).push(c);
    }
    return map;
  }, [catsResp]);

  const getValue = (cat: CategoryDef, ch: Channel) =>
    draft[cat.key]?.[ch] ?? cat.defaults[ch];

  const setValue = (cat: CategoryDef, ch: Channel, v: boolean) => {
    setDraft((d) => {
      const current = d[cat.key] || { ...cat.defaults };
      return { ...d, [cat.key]: { ...current, [ch]: v } };
    });
  };

  if (catsLoading || defaultsLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 overflow-y-auto">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          These are workspace-wide defaults. Each user can override them on their personal{" "}
          <strong>Notifications</strong> page.
        </AlertDescription>
      </Alert>

      {Object.entries(grouped).map(([group, cats]) => (
        <Card key={group} data-testid={`card-defaults-group-${group.replace(/\s+/g, "-").toLowerCase()}`}>
          <CardHeader>
            <CardTitle className="text-base">{group}</CardTitle>
            <CardDescription>
              Default delivery for new users in this workspace.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 gap-y-3 items-center">
              <div className="text-xs font-semibold text-muted-foreground">Event</div>
              <div className="text-xs font-semibold text-muted-foreground text-center w-16">In-app</div>
              <div className="text-xs font-semibold text-muted-foreground text-center w-16">Email</div>
              <div className="text-xs font-semibold text-muted-foreground text-center w-16">Push</div>
              {cats.map((c) => (
                <div key={c.key} className="contents">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{c.label}</div>
                    <div className="text-xs text-muted-foreground">{c.description}</div>
                  </div>
                  <div className="flex justify-center">
                    <Switch
                      checked={getValue(c, "inApp")}
                      onCheckedChange={(v) => setValue(c, "inApp", v)}
                      data-testid={`admin-switch-${c.key}-inApp`}
                    />
                  </div>
                  <div className="flex justify-center">
                    <Switch
                      checked={getValue(c, "email")}
                      onCheckedChange={(v) => setValue(c, "email", v)}
                      data-testid={`admin-switch-${c.key}-email`}
                    />
                  </div>
                  <div className="flex justify-center">
                    <Switch
                      checked={getValue(c, "push")}
                      onCheckedChange={(v) => setValue(c, "push", v)}
                      data-testid={`admin-switch-${c.key}-push`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      <div className="sticky bottom-0 bg-background/95 backdrop-blur py-3 flex justify-end">
        <Button
          size="lg"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          data-testid="button-save-defaults"
        >
          {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Save workspace defaults
        </Button>
      </div>
    </div>
  );
}
